/**
 * AdminTenantDetail.tsx — strona szczegolow tenanta (F7a T7.3).
 *
 * Route: /tenants/:tenantId
 * Data: `GET /api/admin/tenants/:id` via React Query.
 *
 * Layout:
 *   - Header: breadcrumb "← Klienci", tytul (name + monospace code/slug), badges
 *     (status primary sub + dbMode + soft-deleted jesli gcrecord).
 *   - Tabs (7): Overview | Moduły | Firmy | Użytkownicy | Faktury | Migracja z WF | Audit.
 *   - Overview: 3 karty counts + lista subskrypcji + lista ostatnich 5 audit.
 *   - Pozostale taby: placeholder "Wkrótce (T7.4+)" ze wskazowka co tam bedzie.
 *
 * T7.4–T7.8 wypelnia konkretne taby tresci.
 */

import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { Badge, type BadgeColor } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Layout } from '@/components/Layout';
import { Skeleton } from '@/components/Skeleton';
import { Tabs, type TabsItem } from '@/components/Tabs';
import { TenantActionsToolbar } from '@/components/tenant-detail/TenantActionsToolbar';
import { TenantAuditTab } from '@/components/tenant-detail/TenantAuditTab';
import { TenantCaretakerCard } from '@/components/tenant-detail/TenantCaretakerCard';
import { TenantClientDbSetup } from '@/components/tenant-detail/TenantClientDbSetup';
import { TenantCompaniesTab } from '@/components/tenant-detail/TenantCompaniesTab';
import { TenantDatabaseCard } from '@/components/tenant-detail/TenantDatabaseCard';
import { TenantInvoicesTab } from '@/components/tenant-detail/TenantInvoicesTab';
import { TenantLegacyMigrationTab } from '@/components/tenant-detail/TenantLegacyMigrationTab';
import { TenantModulesTab } from '@/components/tenant-detail/TenantModulesTab';
import { TenantUsersTab } from '@/components/tenant-detail/TenantUsersTab';
import { api } from '@/lib/api';
import { humanizeAuditAction } from '@/lib/humanize-audit-action';

interface TenantDetail {
  tenant: {
    id: string;
    code: string;
    slug: string;
    name: string;
    dbMode: 'shared' | 'dedicated';
    dbStatus: string;
    dbServer?: { id: string; name: string; host: string | null; status: string } | null;
    /** Tenant czekający na bazę ('awaiting_db'): serwer klienta wybrany w karcie, jeśli już podany. */
    pendingDbServerId?: string | null;
    /** Nieudana próba zostawiła bazę u klienta do ręcznego usunięcia. */
    pendingOrphanDb?: boolean;
    createdAt: string;
    gcrecord: string | null;
  };
  counts: {
    companies: number | null;
    users: number | null;
    modules: number;
  };
  subscriptions: Array<{
    moduleCode: string;
    moduleName: string;
    status: string | null;
    trialEndsAt: string | null;
    activatedAt: string | null;
    cancelledAt: string | null;
  }>;
  recentAudit: Array<{
    id: string;
    action: string;
    actorEmail: string | null;
    createdAt: string;
    details: unknown;
  }>;
}

const STATUS_TO_COLOR: Record<string, BadgeColor> = {
  active: 'green',
  trialing: 'blue',
  past_due: 'amber',
  cancelled: 'gray',
  suspended: 'red',
};

const DB_STATUS_TO_COLOR: Record<string, BadgeColor> = {
  awaiting_db: 'amber',
  outdated: 'amber',
  suspended: 'red',
  failed: 'red',
  provisioning: 'blue',
  migrating: 'blue',
};

const DB_STATUS_LABEL_KEY: Record<string, string> = {
  awaiting_db: 'admin.tenants.detail.dbStatus.awaiting_db',
  outdated: 'admin.tenants.detail.dbStatus.outdated',
  suspended: 'admin.tenants.detail.dbStatus.suspended',
  provisioning: 'admin.tenants.detail.dbStatus.provisioning',
  failed: 'admin.tenants.detail.dbStatus.failed',
  migrating: 'admin.tenants.detail.dbStatus.migrating',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function AdminTenantDetail() {
  const { t } = useTranslation();
  const { tenantId } = useParams<{ tenantId: string }>();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error, refetch } = useQuery<TenantDetail>({
    queryKey: ['admin', 'tenant', tenantId],
    queryFn: async () => {
      const res = await api.get<TenantDetail>(`/admin/tenants/${tenantId}`);
      return res.data;
    },
    enabled: !!tenantId,
    retry: false,
  });

  const handleActionSuccess = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
  };

  const statusLabel = (status: string | null): string => {
    if (!status) return '—';
    const key = `admin.tenants.subscriptionStatus.${status}`;
    const translated = t(key);
    return translated === key ? status : translated;
  };

  const dbStatusLabel = (status: string): string => {
    const key = DB_STATUS_LABEL_KEY[status];
    return key ? t(key) : status;
  };

  const countValue = (v: number | null) =>
    v === null ? (
      <>
        <span className="text-text-muted">—</span>
        <p className="mt-1 text-[11px] font-normal text-text-muted">
          {t('admin.tenants.detail.counts.notAvailable')}
        </p>
      </>
    ) : (
      <span>{v}</span>
    );

  if (isLoading) {
    return (
      <Layout title={t('admin.tenants.title')}>
        <section className="px-6 py-8">
          <Skeleton height={24} width={120} className="mb-4" />
          <Skeleton height={40} width={320} className="mb-3" />
          <Skeleton height={20} width={220} className="mb-6" />
          <div className="flex gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} height={36} width={96} />
            ))}
          </div>
        </section>
      </Layout>
    );
  }

  if (isError) {
    const is404 = axios.isAxiosError(error) && error.response?.status === 404;
    return (
      <Layout title={t('admin.tenants.title')}>
        <section className="mx-auto max-w-3xl px-6 py-8">
          <Link to="/tenants" className="mb-4 inline-block text-sm text-accent-600 hover:underline">
            {t('admin.tenants.detail.backToList')}
          </Link>
          <Card>
            <h2 className="mb-2 text-lg font-semibold text-text-primary">
              {is404
                ? t('admin.tenants.detail.errors.notFound')
                : t('admin.tenants.detail.errors.generic')}
            </h2>
            {is404 ? (
              <p className="text-sm text-text-secondary">
                {t('admin.tenants.detail.errors.notFoundHint')}
              </p>
            ) : (
              <>
                <p className="text-sm text-text-secondary">
                  {error instanceof Error ? error.message : ''}
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-3"
                  onClick={() => void refetch()}
                >
                  {t('admin.tenants.detail.errors.retry')}
                </Button>
              </>
            )}
          </Card>
        </section>
      </Layout>
    );
  }

  if (!data) return null;

  const { tenant, counts, subscriptions, recentAudit } = data;
  const primarySub = subscriptions[0];
  // Baza na serwerze klienta jeszcze nie utworzona: poza Przeglądem nie ma czego pokazać ani edytować.
  const awaitingDb = tenant.dbStatus === 'awaiting_db' && tenant.gcrecord === null;

  const overviewContent = (
    <div className="flex flex-col gap-6">
      {awaitingDb && (
        <TenantClientDbSetup
          tenantId={tenant.id}
          tenantName={tenant.name}
          pendingDbServerId={tenant.pendingDbServerId ?? null}
          orphanDb={tenant.pendingOrphanDb ?? false}
        />
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card padding="md">
          <p className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
            {t('admin.tenants.detail.counts.companies')}
          </p>
          <div className="mt-2 text-3xl font-semibold text-text-primary">
            {countValue(counts.companies)}
          </div>
        </Card>
        <Card padding="md">
          <p className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
            {t('admin.tenants.detail.counts.users')}
          </p>
          <div className="mt-2 text-3xl font-semibold text-text-primary">
            {countValue(counts.users)}
          </div>
        </Card>
        <Card padding="md">
          <p className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
            {t('admin.tenants.detail.counts.modules')}
          </p>
          <div className="mt-2 text-3xl font-semibold text-text-primary">{counts.modules}</div>
        </Card>
      </div>

      <Card padding="md">
        <h3 className="mb-3 text-sm font-semibold text-text-primary">
          {t('admin.tenants.detail.section.subscriptions')}
        </h3>
        {subscriptions.length === 0 ? (
          <p className="text-sm text-text-muted">
            {t('admin.tenants.detail.section.subscriptionsEmpty')}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {subscriptions.map((s) => (
              <li
                key={s.moduleCode}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border-secondary px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-text-tertiary">{s.moduleCode}</span>
                  <span className="text-sm font-medium text-text-primary">{s.moduleName}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Badge color={STATUS_TO_COLOR[s.status ?? ''] ?? 'gray'}>
                    {statusLabel(s.status)}
                  </Badge>
                  {s.status === 'trialing' && s.trialEndsAt && (
                    <span className="text-xs text-text-muted">
                      {t('admin.tenants.detail.trialEnds', { date: formatDate(s.trialEndsAt) })}
                    </span>
                  )}
                  {s.status === 'active' && s.activatedAt && (
                    <span className="text-xs text-text-muted">
                      {t('admin.tenants.detail.activatedOn', {
                        date: formatDate(s.activatedAt),
                      })}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <TenantCaretakerCard tenantId={tenant.id} />

      {tenant.dbMode === 'dedicated' && !awaitingDb && (
        <TenantDatabaseCard
          tenantId={tenant.id}
          dbStatus={tenant.dbStatus}
          dbServer={tenant.dbServer ?? null}
        />
      )}

      <Card padding="md">
        <h3 className="mb-3 text-sm font-semibold text-text-primary">
          {t('admin.tenants.detail.section.recentAudit')}
        </h3>
        {recentAudit.length === 0 ? (
          <p className="text-sm text-text-muted">
            {t('admin.tenants.detail.section.recentAuditEmpty')}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {recentAudit.map((e) => (
              <li
                key={e.id}
                className="rounded-lg border border-border-secondary px-3 py-2 text-sm"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-xs text-text-muted">{formatDateTime(e.createdAt)}</span>
                  <span className="text-xs text-text-secondary" title={e.action}>
                    {humanizeAuditAction(e.action, t).label}
                  </span>
                  {e.actorEmail && (
                    <span className="text-xs text-text-tertiary">— {e.actorEmail}</span>
                  )}
                </div>
                {e.details !== null && e.details !== undefined && (
                  <pre className="mt-1 overflow-x-auto rounded bg-bg-tertiary px-2 py-1 text-[11px] text-text-secondary">
                    {JSON.stringify(e.details)}
                  </pre>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );

  const allItems: TabsItem[] = [
    {
      value: 'overview',
      label: t('admin.tenants.detail.tabs.overview'),
      content: overviewContent,
    },
    {
      value: 'modules',
      label: t('admin.tenants.detail.tabs.modules'),
      content: tenantId ? <TenantModulesTab tenantId={tenantId} /> : null,
    },
    {
      value: 'companies',
      label: t('admin.tenants.detail.tabs.companies'),
      content: tenantId ? <TenantCompaniesTab tenantId={tenantId} /> : null,
    },
    {
      value: 'users',
      label: t('admin.tenants.detail.tabs.users'),
      content: tenantId ? <TenantUsersTab tenantId={tenantId} /> : null,
    },
    {
      value: 'invoices',
      label: t('admin.tenants.detail.tabs.invoices'),
      content: tenantId ? <TenantInvoicesTab tenantId={tenantId} /> : null,
    },
    {
      value: 'legacy-migration',
      label: t('admin.tenants.detail.tabs.legacyMigration'),
      content: tenantId ? <TenantLegacyMigrationTab tenantId={tenantId} /> : null,
    },
    {
      value: 'audit',
      label: t('admin.tenants.detail.tabs.audit'),
      content: tenantId ? <TenantAuditTab tenantId={tenantId} /> : null,
    },
  ];
  const items = awaitingDb
    ? allItems.map((i) => (i.value === 'overview' ? i : { ...i, disabled: true }))
    : allItems;

  return (
    <Layout title={tenant.name}>
      <section className="px-6 py-8">
        <Link to="/tenants" className="mb-3 inline-block text-sm text-accent-600 hover:underline">
          {t('admin.tenants.detail.backToList')}
        </Link>

        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-text-primary">{tenant.name}</h2>
          <p className="mt-1 font-mono text-sm text-text-tertiary">
            {tenant.code} · {tenant.slug}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {primarySub && (
              <Badge color={STATUS_TO_COLOR[primarySub.status ?? ''] ?? 'gray'}>
                {statusLabel(primarySub.status)}
              </Badge>
            )}
            <Badge color={tenant.dbMode === 'dedicated' ? 'accent' : 'gray'}>{tenant.dbMode}</Badge>
            {tenant.dbStatus !== 'active' && (
              <Badge color={DB_STATUS_TO_COLOR[tenant.dbStatus] ?? 'gray'}>
                {dbStatusLabel(tenant.dbStatus)}
              </Badge>
            )}
            {tenant.dbServer && (
              <Badge color="accent">
                {t('admin.tenants.detail.dbStatus.serverBadge', { name: tenant.dbServer.name })}
              </Badge>
            )}
            {tenant.gcrecord && <Badge color="red">{t('admin.tenants.detail.softDeleted')}</Badge>}
          </div>
          <p className="mt-2 text-xs text-text-muted">
            {t('admin.tenants.detail.createdAt')}: {formatDate(tenant.createdAt)}
          </p>
        </div>

        {tenantId && (
          <TenantActionsToolbar
            tenantId={tenantId}
            tenantName={tenant.name}
            isSoftDeleted={tenant.gcrecord !== null}
            dbUnavailable={['awaiting_db', 'provisioning', 'failed'].includes(tenant.dbStatus)}
            subscriptions={subscriptions}
            onSuccess={handleActionSuccess}
          />
        )}

        {awaitingDb && (
          <div
            role="status"
            data-testid="tenant-awaiting-db-banner"
            className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200"
          >
            <p className="font-medium">{t('admin.tenants.detail.clientDb.bannerTitle')}</p>
            <p className="mt-0.5">{t('admin.tenants.detail.clientDb.bannerText')}</p>
          </div>
        )}

        <Tabs items={items} defaultValue="overview" idPrefix="tenant-detail" />
      </section>
    </Layout>
  );
}
